import * as Dialog from '@radix-ui/react-dialog';
import { useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FormError, Label } from '@/components/ui/field';
import { RequiredFieldsNote } from '@/components/ui/form-section';
import { useClient, useClients } from '@/hooks/clients';
import { useCreateJob } from '@/hooks/jobs';
import { usePropertyCategories } from '@/hooks/property-categories';
import { useServiceTypes } from '@/hooks/catalog-admin';
import { ApiError } from '@/lib/api';
import { useToast } from '@/lib/toast-context';
import { AssignmentPicker } from '@/pages/admin/jobs/AssignmentPicker';
import type { JobDetail } from '@shared/schemas/job';
import { MAX_PAGE_SIZE } from '@shared/schemas/pagination';

/*
 * Create a job — ROADMAP.md §4.3's order: client → property → category →
 * service type → date and time → duration → assignment.
 *
 * The cascade is real, not cosmetic: properties belong to the chosen client,
 * the category defaults from the chosen property, the duration defaults from
 * the chosen service. Each step narrows the next, which is why this is one
 * form rather than a wizard — the admin can see and correct any step without
 * walking back through the others.
 *
 * Assignment is optional here. A job created without a crew is born pending;
 * one created with a crew is born scheduled (business-rules.md §3).
 */

const SELECT_CLASSES =
  'min-h-control w-full rounded-sm border border-ink-300 bg-white px-3 text-body disabled:bg-paper-50 disabled:text-ink-500';

function SelectField({
  label,
  name,
  value,
  onChange,
  disabled,
  required,
  hint,
  error,
  children,
}: {
  label: string;
  name: string;
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  required?: boolean;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={`job-${name}`} required={required}>
        {label}
      </Label>
      <select
        id={`job-${name}`}
        name={name}
        value={value}
        disabled={disabled}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        className={SELECT_CLASSES}
      >
        {children}
      </select>
      {hint ? <p className="text-meta text-ink-500">{hint}</p> : null}
      {error ? (
        <p role="alert" className="text-meta text-status-priority-text">
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function JobCreateDialog({
  open,
  onOpenChange,
  onCreated,
  initialDate,
  initialTime,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: (created: JobDetail) => void;
  /** The calendar pre-fills these when a slot is clicked (4.7). */
  initialDate?: string;
  initialTime?: string;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-xl -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-ink-300 bg-white p-6 shadow-pop">
          <JobCreateForm
            onCreated={onCreated}
            initialDate={initialDate}
            initialTime={initialTime}
            onDone={() => {
              onOpenChange(false);
            }}
          />
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function JobCreateForm({
  onDone,
  onCreated,
  initialDate,
  initialTime,
}: {
  onDone: () => void;
  onCreated?: (created: JobDetail) => void;
  initialDate?: string;
  initialTime?: string;
}) {
  const toast = useToast();

  const [clientId, setClientId] = useState('');
  const [propertyId, setPropertyId] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [serviceTypeId, setServiceTypeId] = useState('');
  const [scheduledDate, setScheduledDate] = useState(initialDate ?? '');
  const [scheduledTime, setScheduledTime] = useState(initialTime ?? '');
  const [duration, setDuration] = useState('120');
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [employeeIds, setEmployeeIds] = useState<string[]>([]);

  /*
   * **Page 1 was being treated as the whole list, and that was a real defect**
   * — recorded under PROGRESS.md §Known problems since phase 6. `DEFAULT_PAGE_SIZE`
   * is 25, so past the twenty-fifth client a job simply could not be created for
   * them and nothing on screen said why. It surfaced when accumulated test data
   * pushed the development database past 25 and the phase-4 Playwright spec
   * started timing out on `selectOption`.
   *
   * Two different fixes, because they are two different problems:
   *
   *   - **Clients grow without limit**, so the answer is search rather than a
   *     bigger number. The box below queries the server, which is what makes
   *     every client reachable no matter how many there are. `MAX_PAGE_SIZE`
   *     keeps the unsearched list at a hundred, which covers this business
   *     today and is a ceiling rather than a promise.
   *   - **The reference lists are genuinely small** — checklists and services
   *     are a handful each and bounded by what the office maintains. They only
   *     needed a page size that admits it.
   */
  const [clientSearch, setClientSearch] = useState('');
  const clients = useClients({
    sort: 'name',
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: clientSearch,
    activity: 'active',
    highPriorityOnly: false,
  });
  const clientDetail = useClient(clientId === '' ? undefined : clientId);
  const categories = usePropertyCategories({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: '',
    activity: 'active',
  });
  const services = useServiceTypes({
    page: 1,
    pageSize: MAX_PAGE_SIZE,
    search: '',
    activity: 'active',
  });

  const create = useCreateJob();
  const error = create.error instanceof ApiError ? create.error : null;
  const fieldErrors = error?.fieldErrors ?? {};

  const properties = (clientDetail.data?.properties ?? []).filter((entry) => entry.isActive);

  function pickClient(id: string) {
    setClientId(id);
    // A different client means a different set of properties — a stale pick
    // would schedule a clean at the wrong house.
    setPropertyId('');
  }

  function pickProperty(id: string) {
    setPropertyId(id);
    const property = properties.find((entry) => entry.id === id);
    // The property knows its own checklist template; picking it fills the
    // category and leaves the admin free to override.
    if (property?.categoryId) setCategoryId(property.categoryId);
  }

  function pickService(id: string) {
    setServiceTypeId(id);
    const service = (services.data?.data ?? []).find((entry) => entry.id === id);
    if (service) setDuration(String(service.defaultDurationMinutes));
  }

  const durationMinutes = Number(duration);
  const checkParams =
    scheduledDate !== '' && scheduledTime !== '' && Number.isFinite(durationMinutes)
      ? {
          date: scheduledDate,
          time: scheduledTime,
          durationMinutes,
          // Both feed the §4 scoring: history with this client, and the skills
          // and position this service wants. Passed only once chosen — the
          // ranking says out loud when it has not scored one of them, rather
          // than quietly leaving it out.
          ...(clientId === '' ? {} : { clientId }),
          ...(serviceTypeId === '' ? {} : { serviceTypeId }),
          ...(propertyId === '' ? {} : { propertyId }),
        }
      : null;

  function submit(event: FormEvent) {
    event.preventDefault();

    create.mutate(
      {
        clientId,
        propertyId,
        categoryId: categoryId === '' ? null : categoryId,
        serviceTypeId,
        scheduledDate,
        scheduledTime,
        estimatedDurationMinutes: durationMinutes,
        specialInstructions,
        employeeIds,
      },
      {
        onSuccess: (created) => {
          toast.success(
            employeeIds.length > 0
              ? `Created ${created.jobNumber}, scheduled.`
              : `Created ${created.jobNumber}. It is pending until someone is assigned.`,
          );
          onCreated?.(created);
          onDone();
        },
      },
    );
  }

  return (
    <>
      <Dialog.Title className="text-h2 text-ink-900">Create a job</Dialog.Title>
      <Dialog.Description className="mt-1 text-body text-ink-500">
        Assigning a crew now schedules it; leaving them off keeps it pending.
      </Dialog.Description>

      <form onSubmit={submit} className="mt-5 space-y-4">
        <RequiredFieldsNote />

        {error && Object.keys(fieldErrors).length === 0 ? (
          <FormError>{error.message}</FormError>
        ) : null}

        <div className="space-y-2">
          {/*
            Searches on the **server**, which is what makes every client
            reachable however many there are. Without it the dropdown showed a
            page and called it the list — see the note beside `useClients` above.
          */}
          {/*
            Deliberately **not** labelled "Find a client": the select below is
            labelled "Client", and two controls whose labels contain the same
            word are two controls a screen reader — and a test — cannot tell
            apart. The `dialog.getByLabel('Client')` in the phase-4 spec is what
            found it.
          */}
          <Field
            label="Search by name or phone"
            name="clientSearch"
            type="search"
            value={clientSearch}
            onChange={(event) => {
              setClientSearch(event.target.value);
            }}
            placeholder="Name, business, phone or email"
            hint={
              clients.data !== undefined && clients.data.total > clients.data.data.length
                ? `Showing ${String(clients.data.data.length)} of ${String(clients.data.total)} — type to narrow it down.`
                : undefined
            }
          />

          <SelectField
            label="Client"
            name="clientId"
            required
            value={clientId}
            onChange={pickClient}
            error={fieldErrors.clientId}
          >
            <option value="">Choose a client</option>
            {(clients.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.displayName}
              </option>
            ))}
          </SelectField>
        </div>

        <SelectField
          label="Property"
          name="propertyId"
          required
          value={propertyId}
          onChange={pickProperty}
          disabled={clientId === ''}
          hint={
            clientId !== '' && !clientDetail.isPending && properties.length === 0
              ? 'This client has no active properties — add one on their page first.'
              : 'The address the crew drives to. It is kept with the job even if the property is edited later.'
          }
          error={fieldErrors.propertyId}
        >
          <option value="">
            {clientId === '' ? 'Choose a client first' : 'Choose a property'}
          </option>
          {properties.map((entry) => (
            <option key={entry.id} value={entry.id}>
              {entry.nickname ? `${entry.nickname} — ${entry.fullAddress}` : entry.fullAddress}
            </option>
          ))}
        </SelectField>

        <div className="grid gap-4 sm:grid-cols-2">
          <SelectField
            label="Checklist"
            name="categoryId"
            value={categoryId}
            onChange={setCategoryId}
            hint="Copied onto the job when it is created — later template edits leave it alone."
            error={fieldErrors.categoryId}
          >
            <option value="">No checklist</option>
            {(categories.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </SelectField>

          <SelectField
            label="Service"
            name="serviceTypeId"
            required
            value={serviceTypeId}
            onChange={pickService}
            error={fieldErrors.serviceTypeId}
          >
            <option value="">Choose a service</option>
            {(services.data?.data ?? []).map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </SelectField>
        </div>

        <div className="grid gap-4 sm:grid-cols-3">
          <Field
            label="Date"
            name="scheduledDate"
            type="date"
            required
            value={scheduledDate}
            error={fieldErrors.scheduledDate}
            onChange={(event) => {
              setScheduledDate(event.target.value);
            }}
          />
          <Field
            label="Start time"
            name="scheduledTime"
            type="time"
            required
            value={scheduledTime}
            error={fieldErrors.scheduledTime}
            onChange={(event) => {
              setScheduledTime(event.target.value);
            }}
          />
          <Field
            label="Duration (minutes)"
            name="estimatedDurationMinutes"
            type="number"
            min={15}
            max={1440}
            step={15}
            required
            value={duration}
            error={fieldErrors.estimatedDurationMinutes}
            onChange={(event) => {
              setDuration(event.target.value);
            }}
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="job-specialInstructions">Special instructions</Label>
          <textarea
            id="job-specialInstructions"
            name="specialInstructions"
            rows={2}
            value={specialInstructions}
            onChange={(event) => {
              setSpecialInstructions(event.target.value);
            }}
            placeholder="Focus areas, pets, parking…"
            className="w-full rounded-sm border border-ink-300 bg-white px-3 py-2 text-body placeholder:text-ink-500"
          />
        </div>

        <fieldset>
          <legend className="text-label text-ink-900">Crew</legend>
          <p className="mb-2 mt-1 text-meta text-ink-500">
            The first person picked is the lead. Leave everyone off to create the job as pending.
          </p>
          <AssignmentPicker params={checkParams} value={employeeIds} onChange={setEmployeeIds} />
        </fieldset>

        <div className="flex justify-end gap-2 pt-2">
          <Dialog.Close asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </Dialog.Close>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create job'}
          </Button>
        </div>
      </form>
    </>
  );
}
